import { getStore } from '~/mobx/AppStore';
import getDateFromNorwegianDateString from '~/utils/datetime/getDateFromNorwegianDateString';

export const NO_ACTIVE_ELECTION_ERROR_REASON = {
    NO_ELECTION: 'no_election',
    NO_REGION: 'no_region',
    PAST_ELECTION_SAME_YEAR: 'past_election_same_year',
    PAST_ELECTION_DIFF_YEAR: 'past_election_diff_year',
    FUTURE_ELECTION_SAME_YEAR: 'future_election_same_year',
    FUTURE_ELECTION_DIFF_YEAR: 'future_election_diff_year',
} as const;

type ValueOfNoActiveElectionErrorReason =
    (typeof NO_ACTIVE_ELECTION_ERROR_REASON)[keyof typeof NO_ACTIVE_ELECTION_ERROR_REASON];

export type NoActiveElectionError = {
    reason: ValueOfNoActiveElectionErrorReason;
};

export type NoActiveElectionErrorReasons = {
    [key in (typeof NO_ACTIVE_ELECTION_ERROR_REASON)[keyof typeof NO_ACTIVE_ELECTION_ERROR_REASON]]: boolean;
};

// Possible error reasons
// 1. Election exists but is past and not in the same year
// 2. Election exists but is past but still in the same year
// 3. Election exists and is in the future
// 4. Election exists and is in the future and not in the same year
// 5. No active election at all
// 6. No region
// Check if election active or not by datetime using start_date and end_date value of election object
const getNoActiveElectionError = (): {
    isError: boolean;
    reasons: NoActiveElectionErrorReasons;
} => {
    const { ballotStore, previewStore } = getStore();
    const reasons = {
        [NO_ACTIVE_ELECTION_ERROR_REASON.NO_ELECTION]: false,
        [NO_ACTIVE_ELECTION_ERROR_REASON.NO_REGION]: false,
        [NO_ACTIVE_ELECTION_ERROR_REASON.PAST_ELECTION_SAME_YEAR]: false,
        [NO_ACTIVE_ELECTION_ERROR_REASON.PAST_ELECTION_DIFF_YEAR]: false,
        [NO_ACTIVE_ELECTION_ERROR_REASON.FUTURE_ELECTION_SAME_YEAR]: false,
        [NO_ACTIVE_ELECTION_ERROR_REASON.FUTURE_ELECTION_DIFF_YEAR]: false,
    };

    // In preview mode, we don't care if election active or not
    const isPreview = previewStore.isInitialized;
    if (isPreview) {
        return {
            isError: false,
            reasons: {} as NoActiveElectionErrorReasons,
        };
    }

    const { election, selectedElectionId, region, selectedRegionId } = ballotStore;

    const now = new Date();

    if (!election || !selectedElectionId) {
        reasons[NO_ACTIVE_ELECTION_ERROR_REASON.NO_ELECTION] = true;
    }

    if (!selectedRegionId || !region) {
        reasons[NO_ACTIVE_ELECTION_ERROR_REASON.NO_REGION] = true;
    }

    const startDate = getDateFromNorwegianDateString(election?.electionStartDate || '');
    const endDate = getDateFromNorwegianDateString(election?.electionEndDate || '');

    if (startDate && now < startDate) {
        if (now.getFullYear() !== startDate.getFullYear()) {
            reasons[NO_ACTIVE_ELECTION_ERROR_REASON.FUTURE_ELECTION_DIFF_YEAR] = true;
        } else {
            reasons[NO_ACTIVE_ELECTION_ERROR_REASON.FUTURE_ELECTION_SAME_YEAR] = true;
        }
    }

    if (endDate && now > endDate) {
        if (startDate && now.getFullYear() === startDate.getFullYear()) {
            reasons[NO_ACTIVE_ELECTION_ERROR_REASON.PAST_ELECTION_SAME_YEAR] = true;
        } else {
            reasons[NO_ACTIVE_ELECTION_ERROR_REASON.PAST_ELECTION_DIFF_YEAR] = true;
        }
    }

    return {
        isError: Object.values(reasons).some((value) => value === true),
        reasons,
    };
};

export default getNoActiveElectionError;
